import DownloadIcon from "@mui/icons-material/Download";
import HistoryIcon from "@mui/icons-material/History";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { TransferOrderDetailsByTransferIdApiResponse } from "../../../@types/DRO/TransferOrderResponse";
import { TransferOrderPickBody } from "../../../@types/DTO/CreateTransferOrderBody";
import { DownloadPdfApi } from "../../../api/PdfDownload/DownloadPdfApi";
import { TransferOrderApi } from "../../../api/TransferOrder/TransferOrderApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import { TransferOrderHistory } from "../../UI/OrderHistory/TransferOrderHistory";

const CreateTransferOrderComplete: React.FC = () => {
  const params = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const [isSuccess, setIsSuccess] = useState(false);
  const [transferDetails, setTransferDetails] =
    useState<TransferOrderDetailsByTransferIdApiResponse>();
  const [orderId, setOrderId] = useState<number>(-1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [pickedQuantityDrafts, setPickedQuantityDrafts] = useState<
    Record<number, string>
  >({});
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);

  const [permittedActions, setPermittedActions] = useState<string[]>([]);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const getTransferDetails = useCallback(
    async (orderId: number) => {
      setLoading(true);
      if (orderId <= 0) return;
      try {
        const response =
          await TransferOrderApi.api.getTransferOrderDetailsByTransferId(
            orderId
          );
        setTransferDetails(response);
        setPermittedActions(response.data.permittedAction || []);
      } catch (error: any) {
        console.error("Error fetching parts", error);
        setSnackbarMessage(
          error?.response?.data?.message ||
          error?.data?.message ||
          error?.message ||
          "Failed to fetch transfer order details"
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
      setLoading(false);
    },
    [orderId]
  );

  useEffect(() => {
    if (params.orderId && !isNaN(+params.orderId)) {
      setOrderId(+params.orderId);
    }
  }, [params.orderId]);

  useEffect(() => {
    getTransferDetails(orderId);
  }, [orderId, getTransferDetails]);

  const handleMarkPickup = async () => {
    const orderLineDetails = transferDetails?.data.orderLineDetailsList || [];
    const partsList: TransferOrderPickBody["partsList"] = [];

    for (let index = 0; index < orderLineDetails.length; index += 1) {
      const item = orderLineDetails[index];
      const receivedQuantity = Number(
        pickedQuantityDrafts[index] ?? item.quantityReceived ?? 0
      );
      const totalVendorQuantity = (item.vendorAllocations || []).reduce(
        (total, vendor) => total + (Number(vendor.quantity) || 0),
        0
      );

      if (!Number.isFinite(receivedQuantity) || receivedQuantity < 0) {
        setSnackbarMessage("Received quantity must be a valid number (≥ 0).");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      if (receivedQuantity > item.quantityOrdered) {
        setSnackbarMessage("Received quantity cannot exceed Quantity Ordered.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      if (receivedQuantity < totalVendorQuantity) {
        setSnackbarMessage(
          `Picked quantity cannot be less than the total vendor quantity (${totalVendorQuantity}).`
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      partsList.push({
        partId: item.partId,
        quantityReceived: receivedQuantity,
        ...(item.vendorAllocations?.length && {
          vendorAllocations: item.vendorAllocations.map((vendor) => ({
            vendorId: vendor.vendorId,
            quantity: Number(vendor.quantity) || 0,
            vendorName: vendor.vendorName,
          })),
        }),
      });
    }

    setLoading(true);
    try {
      const body: TransferOrderPickBody = { partsList };

      const response = await TransferOrderApi.api.transferOrderPickup(
        orderId,
        body
      );
      console.log(response);
      setIsSuccess(true);
      setSnackbarMessage(
        response?.message || "Transfer order marked as picked up successfully."
      );
      setSnackbarSeverity("success");
    } catch (error: any) {
      console.error("Error marking transfer order as picked up", error);
      setIsSuccess(false);
      setSnackbarMessage(
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        "Failed to mark transfer order as picked up."
      );
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
      setSnackbarOpen(true);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    setLoading(true);
    try {
      let responseMessage: string | undefined;
      if (newStatus === "DRAFT") {
        const details = transferDetails;
        if (!details) {
          setSnackbarMessage("Transfer order details are not loaded.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
          return;
        }

        const response = await TransferOrderApi.api.updateTransferOrder(
          orderId,
          {
            sourceWarehouse: details.data.sourceWarehouse.id,
            destinationWarehouse: details.data.destinationWarehouse.id,
            status: newStatus,
            partsList: details.data.orderLineDetailsList.map((item) => ({
              partId: item.partId,
              quantityOrdered: item.quantityOrdered,
              ...(item.vendorAllocations?.length && {
                vendorAllocations: item.vendorAllocations.map((vendor) => ({
                  vendorId: vendor.vendorId,
                  quantity: Number(vendor.quantity) || 0,
                })),
              }),
            })),
          }
        );
        responseMessage = response?.message;
      } else {
        const response =
          await TransferOrderApi.api.changeTransferOrderStatus(
            orderId,
            newStatus
          );
        responseMessage = response?.message;
      }

      setIsSuccess(true);
      setSnackbarMessage(
        responseMessage ||
          `Transfer order status changed to ${newStatus} successfully.`
      );
      setSnackbarSeverity("success");
    } catch (error: any) {
      console.error(
        `Error changing transfer order status to ${newStatus}`,
        error
      );
      setIsSuccess(false);
      setSnackbarMessage(
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        `Failed to change transfer order status to ${newStatus}.`
      );
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
      setSnackbarOpen(true);
    }
  };
  const handleDownloadPdf = async () => {
    if (orderId <= 0) {
      setSnackbarMessage("Invalid Transfer Order ID");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsDownloading(true);
      const response = await DownloadPdfApi.api.getPdfLinkByTOId(orderId);
      setSnackbarMessage("PDF downloaded successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error("Error downloading PDF:", error);
      setSnackbarMessage(
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        "Failed to download PDF. Please try again."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsDownloading(false);
    }
  };
  const handleQuantityReceivedChange = (index: number, newValue: string) => {
    const quantityOrdered =
      transferDetails?.data.orderLineDetailsList[index]?.quantityOrdered;
    const enteredQuantity = Number(newValue);

    if (
      quantityOrdered !== undefined &&
      newValue !== "" &&
      Number.isFinite(enteredQuantity) &&
      enteredQuantity > quantityOrdered
    ) {
      setSnackbarMessage("Received quantity cannot exceed Quantity Ordered.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setPickedQuantityDrafts((previousDrafts) => ({
      ...previousDrafts,
      [index]: newValue,
    }));
  };

  const handleQuantityReceivedBlur = (index: number) => {
    if (!transferDetails?.data.orderLineDetailsList) return;

    const updatedItems = [...transferDetails.data.orderLineDetailsList];
    const newValue = pickedQuantityDrafts[index];
    if (newValue === undefined) return;

    const quantityOrdered = updatedItems[index].quantityOrdered;

    // Parse the new value (allow 0)
    const newQuantityReceived = Number(newValue);

    // Check if the value is a valid number (including 0)
    if (isNaN(newQuantityReceived) || newQuantityReceived < 0) {
      setSnackbarMessage("Received quantity must be a valid number (≥ 0).");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setPickedQuantityDrafts((previousDrafts) => {
        const nextDrafts = { ...previousDrafts };
        delete nextDrafts[index];
        return nextDrafts;
      });
      return;
    }

    // Still prevent exceeding quantity ordered
    if (newQuantityReceived > quantityOrdered) {
      setSnackbarMessage("Received quantity cannot exceed Quantity Ordered.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setPickedQuantityDrafts((previousDrafts) => {
        const nextDrafts = { ...previousDrafts };
        delete nextDrafts[index];
        return nextDrafts;
      });
      return;
    }

    const totalVendorQuantity = (
      (updatedItems[index] as typeof updatedItems[number] & {
        vendorAllocations?: { quantity?: number }[];
      }).vendorAllocations || []
    )
      .reduce(
        (total: number, vendor: any) => total + (Number(vendor.quantity) || 0),
        0
      );

    if (newQuantityReceived < totalVendorQuantity) {
      setSnackbarMessage(
        `Picked quantity cannot be less than the total vendor quantity (${totalVendorQuantity}).`
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setPickedQuantityDrafts((previousDrafts) => {
        const nextDrafts = { ...previousDrafts };
        delete nextDrafts[index];
        return nextDrafts;
      });
      return;
    }

    // Update the state (allows 0)
    updatedItems[index].quantityReceived = newQuantityReceived;

    setTransferDetails({
      ...transferDetails,
      data: {
        ...transferDetails.data,
        orderLineDetailsList: updatedItems,
      },
    });

    setPickedQuantityDrafts((previousDrafts) => {
      const nextDrafts = { ...previousDrafts };
      delete nextDrafts[index];
      return nextDrafts;
    });
  };

  // Update a single vendor's allocated quantity within an item's vendorAllocations list.
  // NOTE: assumes each item in orderLineDetailsList has a `vendorAllocations` array shaped as:
  //   { vendorId: number | string; vendorName: string; quantity: number }[]
  // Rename fields here (and in the type) to match the actual API response shape.
  const handleVendorQuantityChange = (
    itemIndex: number,
    vendorIndex: number,
    newValue: string
  ) => {
    if (!transferDetails?.data.orderLineDetailsList) return;

    const newQuantity = Number(newValue);
    if (isNaN(newQuantity) || newQuantity < 0) {
      setSnackbarMessage("Vendor quantity must be a valid number (≥ 0).");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const updatedItems = [...transferDetails.data.orderLineDetailsList];
    const item = { ...(updatedItems[itemIndex] as any) };
    const quantityOrdered = item.quantityOrdered;

    if (newQuantity > quantityOrdered) {
      setSnackbarMessage("Vendor quantity cannot exceed Quantity Ordered.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const vendorAllocations = [...(item.vendorAllocations || [])];

    vendorAllocations[vendorIndex] = {
      ...vendorAllocations[vendorIndex],
      quantity: newQuantity,
    };

    item.vendorAllocations = vendorAllocations;
    updatedItems[itemIndex] = item;

    setTransferDetails({
      ...transferDetails,
      data: {
        ...transferDetails.data,
        orderLineDetailsList: updatedItems,
      },
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    if (isSuccess) {
      navigate(`/transfer-order${statusFilter ? `?status=${statusFilter}` : ""}`);
    }
  };

  const renderActionButtons = () => {
    const buttons = [];

    if (permittedActions.includes("MOVE_TO_DRAFT")) {
      buttons.push(
        <Button
          key="mark-pickup"
          onClick={() => handleStatusChange("DRAFT")}
          variant="outlined"
          color="primary"
          sx={{
            padding: "8px 20px",
            borderRadius: "32px",
            textTransform: "none",
            fontWeight: "500",
            fontSize: "0.875rem",
            "&:hover": { backgroundColor: "#000000" },
          }}>
          Move to Draft
        </Button>
      );
    }
    // Check for PICKED permission
    if (permittedActions.includes("PICKED")) {
      buttons.push(
        <Button
          key="mark-pickup"
          onClick={handleMarkPickup}
          variant="contained"
          color="primary"
          sx={{
            padding: "8px 20px",
            borderRadius: "32px",
            backgroundColor: "#1E1E1E",
            textTransform: "none",
            fontWeight: "500",
            fontSize: "0.875rem",
            "&:hover": { backgroundColor: "#000000" },
          }}>
          Mark Pickup
        </Button>
      );
    }

    // Check for CANCELED permission
    if (permittedActions.includes("CANCELED")) {
      buttons.push(
        <Button
          key="cancel"
          onClick={() => handleStatusChange("CANCELED")}
          variant="outlined"
          sx={{
            padding: "8px 20px",
            borderRadius: "32px",
            borderColor: "#FF0000",
            color: "#FF0000",
            textTransform: "none",
            fontWeight: "500",
            fontSize: "0.875rem",
            "&:hover": {
              borderColor: "#CC0000",
              backgroundColor: "rgba(255, 0, 0, 0.04)",
            },
          }}>
          Cancel Order
        </Button>
      );
    }

    // Check for DRAFT permission (for editing)
    if (permittedActions.includes("DRAFT")) {
      buttons.push(
        <Button
          key="edit"
          onClick={() => navigate(`/transfer-order/draft/${orderId}${statusFilter ? `?status=${statusFilter}` : ""}`)}
          variant="outlined"
          sx={{
            padding: "8px 20px",
            borderRadius: "32px",
            textTransform: "none",
            fontWeight: "500",
            fontSize: "0.875rem",
          }}>
          Edit Order
        </Button>
      );
    }

    return buttons;
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="w-full p-4">
        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: "600",
                color: "#1E1E1E",
                textAlign: "left",
                fontSize: "1.5rem",
              }}>
              Transfer Order Details
            </Typography>
            {orderId > 0 && (
              <>
                <TransferOrderHistory
                  transferOrderId={orderId}
                  variant="dialog"
                  open={historyDialogOpen}
                  onClose={() => setHistoryDialogOpen(false)}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Button
                    variant="contained"
                    startIcon={
                      isDownloading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <DownloadIcon />
                      )
                    }
                    onClick={handleDownloadPdf}
                    disabled={isDownloading}
                    sx={{
                      backgroundColor: "#1366D9",
                      color: "white",
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      padding: "8px 16px",
                      borderRadius: "8px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      "&:hover": {
                        backgroundColor: "#0f5cc0",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                      },
                      "&:disabled": {
                        backgroundColor: "#B0C4DE",
                      },
                    }}>
                    {isDownloading ? "Downloading..." : "Download PDF"}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<HistoryIcon />}
                    onClick={() => setHistoryDialogOpen(true)}
                    sx={{ textTransform: "none", fontWeight: 500, borderRadius: "8px" }}>
                    History
                  </Button>
                </Box>
              </>
            )}
          </Box>
          <div className="flex items-center bg-[#F8F9FA] px-4 py-3 rounded-lg space-x-2">
            <Link
              to="/home"
              className="text-sm font-medium text-blue-600 hover:text-blue-600 hover:underline transition-colors">
              Dashboard
            </Link>
            <span className="text-sm text-[#646464] mx-1">&gt;</span>
            <Link
              to={`/transfer-order${statusFilter ? `?status=${statusFilter}` : ""}`}
              className="text-sm font-medium text-blue-600 hover:text-blue-600 hover:underline transition-colors">
              Transfer Order List
            </Link>
            <span className="text-sm text-[#646464] mx-1">&gt;</span>
            <span className="text-sm font-medium text-gray-800">
              Transfer Order Details
            </span>
          </div>
        </Box>
        <Paper
          sx={{
            p: 4,
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            backgroundColor: "white",
            maxWidth: "1600px",
            margin: "0 auto",
          }}>
          {/* Form Section */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Source Location
                  </label>
                  <input
                    readOnly
                    className="w-full border-b focus:outline-none p-1 text-sm"
                    value={transferDetails?.data.sourceWarehouse.name ?? ""}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Destination Location
                  </label>
                  <input
                    readOnly
                    className="w-full border-b focus:outline-none p-1 text-sm"
                    value={
                      transferDetails?.data.destinationWarehouse.name ?? ""
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Source TO ID
                  </label>
                  <input
                    readOnly
                    className="w-full border-b focus:outline-none p-1 text-sm"
                    value={transferDetails?.data.parentId ?? "-"}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Created By
                  </label>
                  <input
                    readOnly
                    className="w-full border-b focus:outline-none p-1 text-sm"
                    value={transferDetails?.data.createdBy ?? "N/A"}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Reference Code
                  </label>
                  <input
                    readOnly
                    className="w-full border-b focus:outline-none p-1 text-sm"
                    value={transferDetails?.data.referenceCode ?? "N/A"}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Items Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle1"
              sx={{
                textAlign: "left",
                fontWeight: "600",
                color: "#1E1E1E",
                mb: 2,
                fontSize: "1.125rem",
              }}>
              Items
            </Typography>
            <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 80,
                      }}>
                      #
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 200,
                      }}>
                      Product Description
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 150,
                      }}>
                      Reference Code
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 120,
                      }}>
                      Quantity Ordered
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 120,
                      }}>
                      Quantity Available
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 120,
                      }}>
                      Picked Qty
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 150,
                      }}>
                      Unit Of Measure
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {transferDetails?.data.orderLineDetailsList.map(
                    (item: any, index) => (
                      <React.Fragment key={index}>
                        {/* Main item row */}
                        <TableRow
                          sx={{
                            "&:nth-of-type(odd)": { backgroundColor: "#ffffff" },
                            "&:hover": { backgroundColor: "#e3f2fd" },
                          }}>
                          <TableCell sx={{ fontSize: "0.875rem" }}>
                            {index + 1}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.875rem" }}>
                            {item.description}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.875rem" }}>
                            {item.referenceCode || "-"}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.875rem" }}>
                            {item.quantityOrdered}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.875rem" }}>
                            {item.quantityAvailable}
                          </TableCell>
                          <TableCell>
                            {permittedActions.includes("PICKED") ? (
                              <TextField
                                value={
                                  pickedQuantityDrafts[index] ??
                                  item.quantityReceived ??
                                  ""
                                }
                                onChange={(e) =>
                                  handleQuantityReceivedChange(
                                    index,
                                    e.target.value
                                  )
                                }
                                onBlur={() =>
                                  handleQuantityReceivedBlur(index)
                                }
                                variant="standard"
                                // type="number"
                                inputProps={{
                                  min: 0,
                                  max: item.quantityOrdered,
                                }}
                                InputProps={{
                                  style: { fontSize: "0.875rem" },
                                }}
                              />
                            ) : (
                              item.quantityReceived || "-"
                            )}
                          </TableCell>
                          <TableCell>UNIT</TableCell>
                        </TableRow>

                        {/* Vendor Allocation sub-section (permanently visible when vendor data exists) */}
                        {item.vendorAllocations &&
                          item.vendorAllocations.length > 0 && (
                            <TableRow>
                              <TableCell
                                colSpan={7}
                                sx={{ p: 0, borderBottom: "none" }}>
                                <Box
                                  sx={{
                                    backgroundColor: "#f9f9f9",
                                    border: "1px solid #E0E0E0",
                                    borderRadius: "8px",
                                    mx: 0,
                                    mb: 2,
                                    mt: 1,
                                    px: 2,
                                    py: 1.5,
                                    overflow: "hidden",
                                  }}>
                                  <Table
                                    size="small"
                                    sx={{
                                      backgroundColor: "white",
                                    }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell
                                          sx={{
                                            fontWeight: 600,
                                            fontSize: "0.8rem",
                                            width: "60%",
                                          }}>
                                          Vendor Name
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: 600,
                                            fontSize: "0.8rem",
                                            width: "40%",
                                          }}>
                                          Quantity
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {item.vendorAllocations.map(
                                        (vendor: any, vIndex: number) => (
                                          <TableRow
                                            key={vendor.vendorId ?? vIndex}>
                                            <TableCell
                                              sx={{ fontSize: "0.8rem" }}>
                                              {vendor.vendorName}
                                            </TableCell>
                                            <TableCell
                                              sx={{ fontSize: "0.8rem" }}>
                                              <TextField
                                                value={vendor.quantity ?? ""}
                                                onChange={(e) =>
                                                  handleVendorQuantityChange(
                                                    index,
                                                    vIndex,
                                                    e.target.value
                                                  )
                                                }
                                                variant="standard"
                                                size="small"
                                                inputProps={{
                                                  min: 0,
                                                  max: item.quantityOrdered,
                                                }}
                                                InputProps={{
                                                  style: {
                                                    fontSize: "0.8rem",
                                                  },
                                                }}
                                              />
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                      </React.Fragment>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}>
              {renderActionButtons()}
            </Box>
          </Box>
        </Paper>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default CreateTransferOrderComplete;
